import { Helmet } from "react-helmet-async";
import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Check, X, ListChecks, RefreshCw, Trash2 } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import HolidayEnquiriesPanel from "../components/admin/HolidayEnquiriesPanel";
import MeetingInstructionsPanel from "../components/admin/MeetingInstructionsPanel";
import { BookingActions, EmailHistory } from "../components/admin/BookingActions";
import { useFocusRecord } from "../lib/useFocusRecord";

const STATUS_LABELS = {
  new: "Nouă",
  confirmed: "Confirmată",
  rejected: "Respinsă",
  completed: "Finalizată",
};

const STATUS_STYLES = {
  new: "bg-terracotta text-white",
  confirmed: "bg-forest text-bone",
  rejected: "bg-destructive text-destructive-foreground",
  completed: "bg-moss text-white",
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, confirmed: 0, rejected: 0, completed: 0 });
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [actionMode, setActionMode] = useState(null);
  const openWith = (b, mode) => { setSelected(b); setActionMode(mode); };
  const closeDrawer = () => { setSelected(null); setActionMode(null); };
  const onActionDone = (b) => { setSelected(b); setActionMode(null); load(); };
  const [searchParams] = useSearchParams();
  const focusBookingId = searchParams.get("booking");
  const focusEnquiryId = searchParams.get("enquiry");
  const [tab, setTab] = useState(focusEnquiryId ? "holidays" : "bookings");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, s] = await Promise.all([
        api.get("/bookings", { params: filter === "all" ? {} : { status_filter: filter } }),
        api.get("/bookings/stats"),
      ]);
      setBookings(b.data);
      setStats(s.data);
    } catch {
      toast.error("Nu s-au putut încărca rezervările.");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);
  useFocusRecord(focusBookingId, bookings, loading, setSelected, "row-");

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/bookings/${id}`, { status });
      toast.success(`Status actualizat: ${STATUS_LABELS[status]}`);
      load();
      if (selected?.id === id) setSelected((s) => ({ ...s, status }));
    } catch {
      toast.error("Eroare la actualizare.");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Sigur ștergi cererea?")) return;
    try {
      await api.delete(`/bookings/${id}`);
      toast.success("Cerere ștearsă.");
      load();
      if (selected?.id === id) setSelected(null);
    } catch {
      toast.error("Eroare la ștergere.");
    }
  };

  return (
    <div className="min-h-screen bg-bone" data-testid="admin-dashboard">
      <Helmet><meta name="robots" content="noindex, nofollow" /><title>Admin | Apuseni Active</title></Helmet>
      {/* Top bar */}
      <header className="bg-forest text-bone">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="text-xs uppercase tracking-wider-3 link-underline opacity-80">← Site</Link>
            <div className="hidden md:block w-px h-5 bg-bone/20" />
            <div className="font-display text-xl">Apuseni Active · Admin</div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-xs uppercase tracking-wider-2 opacity-70 hidden sm:inline">{user?.email}</span>
            <button onClick={logout} data-testid="admin-logout" className="flex items-center gap-2 text-xs uppercase tracking-wider-3 link-underline">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      {/* Section tabs */}
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-8">
        <div className="inline-flex border border-bone-deep" data-testid="admin-tabs">
          {[{ k: "bookings", l: "Rezervări experiențe" }, { k: "holidays", l: "E-MTB Holidays 2027" }].map((tb) => (
            <button key={tb.k} onClick={() => setTab(tb.k)} data-testid={`admin-tab-${tb.k}`}
              className={`px-5 py-3 text-xs uppercase tracking-wider-3 transition-colors ${tab === tb.k ? "bg-forest text-bone" : "bg-white text-forest hover:bg-bone-deep"}`}>
              {tb.l}
            </button>
          ))}
        </div>
      </div>

      {tab === "holidays" ? (
        <div className="pt-8"><HolidayEnquiriesPanel focusId={focusEnquiryId} /></div>
      ) : (
      <>
      <MeetingInstructionsPanel />
      {/* Stats */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-bone-deep border border-bone-deep">
          {[
            { k: "total", label: "Total cereri" },
            { k: "new", label: "Noi" },
            { k: "confirmed", label: "Confirmate" },
            { k: "rejected", label: "Respinse" },
            { k: "completed", label: "Finalizate" },
          ].map((s) => (
            <div key={s.k} data-testid={`stat-${s.k}`} className="bg-white p-6">
              <div className="text-[10px] uppercase tracking-wider-3 text-moss">{s.label}</div>
              <div className="font-display text-5xl text-forest mt-3">{stats[s.k] ?? 0}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Filters + table */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-20">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
          <div className="flex flex-wrap gap-1 border border-bone-deep" data-testid="status-filter">
            {["all", "new", "confirmed", "rejected", "completed"].map((s) => (
              <button
                key={s}
                onClick={() => setFilter(s)}
                data-testid={`filter-${s}`}
                className={`px-5 py-2.5 text-xs uppercase tracking-wider-3 transition-colors ${
                  filter === s ? "bg-forest text-bone" : "bg-white text-forest hover:bg-bone-deep"
                }`}
              >
                {s === "all" ? "Toate" : STATUS_LABELS[s]}
              </button>
            ))}
          </div>
          <button onClick={load} data-testid="refresh-btn" className="flex items-center gap-2 text-xs uppercase tracking-wider-3 text-forest link-underline">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Reîmprospătează
          </button>
        </div>

        <div className="bg-white border border-bone-deep overflow-x-auto">
          <table className="w-full text-sm" data-testid="bookings-table">
            <thead className="bg-bone-deep text-forest">
              <tr className="text-left text-[10px] uppercase tracking-wider-3">
                <th className="px-5 py-4">Data primită</th>
                <th className="px-5 py-4">Client</th>
                <th className="px-5 py-4">Experiență</th>
                <th className="px-5 py-4">Data dorită</th>
                <th className="px-5 py-4">Rideri</th>
                <th className="px-5 py-4">Pickup</th>
                <th className="px-5 py-4">Status</th>
                <th className="px-5 py-4">Acțiuni</th>
              </tr>
            </thead>
            <tbody>
              {loading && bookings.length === 0 && (
                <tr><td colSpan={8} className="px-5 py-12 text-center text-moss text-xs uppercase tracking-wider-3">Se încarcă…</td></tr>
              )}
              {!loading && bookings.length === 0 && (
                <tr><td colSpan={8} className="px-5 py-16 text-center text-moss"><ListChecks className="w-6 h-6 mx-auto mb-3 opacity-50" /><div className="text-xs uppercase tracking-wider-3">Nicio cerere pe acest filtru.</div></td></tr>
              )}
              {bookings.map((b) => (
                <tr key={b.id} className={`border-t border-bone-deep hover:bg-bone-deep/40 cursor-pointer ${b.id === focusBookingId ? "bg-terracotta/10 ring-2 ring-inset ring-terracotta" : ""}`} onClick={() => openWith(b, null)} data-testid={`row-${b.id}`}>
                  <td className="px-5 py-4 text-xs text-moss whitespace-nowrap">{new Date(b.created_at).toLocaleString("ro-RO")}</td>
                  <td className="px-5 py-4">
                    <div className="text-forest">{b.full_name}</div>
                    <div className="text-xs text-moss">{b.email}</div>
                  </td>
                  <td className="px-5 py-4 text-forest">{b.experience_name}</td>
                  <td className="px-5 py-4 text-forest">
                    <div>{b.preferred_date}</div>
                    <div className="text-xs text-moss" data-testid={`time-${b.id}`}>{b.meeting_time ? `Întâlnire ${b.meeting_time}` : b.preferred_time ? `Dorită ${b.preferred_time}` : "Oră flexibilă"}</div>
                  </td>
                  <td className="px-5 py-4">{b.number_of_riders}</td>
                  <td className="px-5 py-4">{b.pickup_location}</td>
                  <td className="px-5 py-4">
                    <span className={`inline-block px-2.5 py-1 text-[10px] uppercase tracking-wider-2 ${STATUS_STYLES[b.status]}`}>
                      {STATUS_LABELS[b.status]}
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                      <button title="Confirmă" data-testid={`confirm-${b.id}`} onClick={() => openWith(b, "confirm")} className="p-2 hover:bg-forest hover:text-bone transition-colors"><Check className="w-4 h-4" /></button>
                      <button title="Respinge" data-testid={`reject-${b.id}`} onClick={() => openWith(b, "reject")} className="p-2 hover:bg-destructive hover:text-bone transition-colors"><X className="w-4 h-4" /></button>
                      <button title="Finalizată" data-testid={`complete-${b.id}`} onClick={() => updateStatus(b.id, "completed")} className="p-2 hover:bg-moss hover:text-bone transition-colors"><ListChecks className="w-4 h-4" /></button>
                      <button title="Șterge" data-testid={`delete-${b.id}`} onClick={() => remove(b.id)} className="p-2 hover:bg-destructive hover:text-bone transition-colors"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Detail drawer */}
      {selected && (
        <div className="fixed inset-0 z-50 flex" data-testid="booking-detail-drawer">
          <div className="flex-1 bg-foreground/40" onClick={closeDrawer} />
          <aside className="w-full sm:w-[480px] bg-bone overflow-y-auto">
            <div className="px-6 py-5 bg-forest text-bone flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-wider-3 opacity-70">Cerere de rezervare</div>
                <div className="font-display text-2xl mt-1">{selected.experience_name}</div>
              </div>
              <button onClick={closeDrawer} data-testid="close-drawer" className="text-bone"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-5 text-sm">
              {actionMode && <BookingActions key={`${selected.id}-${actionMode}`} booking={selected} mode={actionMode} onDone={onActionDone} />}
              <DetailRow label="Status" value={
                <span className={`inline-block px-2.5 py-1 text-[10px] uppercase tracking-wider-2 ${STATUS_STYLES[selected.status]}`}>
                  {STATUS_LABELS[selected.status]}
                </span>
              } />
              <DetailRow label="Nume" value={selected.full_name} />
              <DetailRow label="Email" value={<a href={`mailto:${selected.email}`} className="link-underline">{selected.email}</a>} />
              <DetailRow label="Telefon" value={<a href={`tel:${selected.phone}`} className="link-underline">{selected.phone}</a>} />
              <DetailRow label="Data dorită" value={selected.preferred_date} />
              <DetailRow label="Ora dorită de client" value={selected.preferred_time || "Flexibilă (necompletată)"} />
              <DetailRow label="Ora finală de întâlnire" value={selected.meeting_time ? <strong data-testid="detail-meeting-time">{selected.meeting_time}</strong> : <span className="text-moss">Nestabilită</span>} />
              <DetailRow label="Adresa de întâlnire" value={selected.meeting_address ? <span data-testid="detail-meeting-address" className="whitespace-pre-line">{selected.meeting_address}</span> : <span className="text-moss">Nestabilită</span>} />
              {selected.meeting_maps_url && <DetailRow label="Google Maps" value={<a href={selected.meeting_maps_url} target="_blank" rel="noopener noreferrer" data-testid="detail-meeting-maps" className="text-forest underline break-all">Deschide harta</a>} />}
              <DetailRow label="Limba clientului" value={(selected.lang || "ro").toUpperCase()} />
              <DetailRow label="Număr rideri" value={selected.number_of_riders} />
              <DetailRow label="Pickup" value={selected.pickup_location} />
              <DetailRow label="Nivel" value={selected.rider_level} />
              <DetailRow label="Înălțime" value={`${selected.height} cm`} />
              {selected.message && <DetailRow label="Mesaj" value={<span className="whitespace-pre-wrap">{selected.message}</span>} />}
              <DetailRow label="Primită la" value={new Date(selected.created_at).toLocaleString("ro-RO")} />
              <DetailRow label="Istoric e-mailuri" value={<EmailHistory log={selected.email_log} />} />

              <div className="pt-4 grid grid-cols-2 gap-2">
                <button onClick={() => setActionMode("confirm")} data-testid="drawer-confirm-btn" className="btn-lift bg-forest text-bone py-3 text-xs uppercase tracking-wider-3">{selected.confirmation_sent_at ? "Editează întâlnirea" : "Confirmă"}</button>
                <button onClick={() => setActionMode("reject")} data-testid="drawer-reject-btn" className="btn-lift border border-destructive text-destructive py-3 text-xs uppercase tracking-wider-3">Respinge</button>
                <button onClick={() => updateStatus(selected.id, "completed")} data-testid="drawer-complete-btn" className="btn-lift bg-moss text-white py-3 text-xs uppercase tracking-wider-3">Finalizată</button>
                <button onClick={() => updateStatus(selected.id, "new")} data-testid="drawer-new-btn" className="btn-lift border border-forest text-forest py-3 text-xs uppercase tracking-wider-3">Marchează nouă</button>
              </div>
            </div>
          </aside>
        </div>
      )}
      </>
      )}
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div className="flex flex-col gap-1 border-b border-bone-deep pb-3">
      <span className="text-[10px] uppercase tracking-wider-3 text-moss">{label}</span>
      <span className="text-forest">{value}</span>
    </div>
  );
}
